import { PropsWithChildren, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { skipToken } from '@reduxjs/toolkit/query'
import { Button } from '@mui/material'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import { useSessionExistsQuery, useStartCardMonitoring } from '@/core/services/dmp-connect-js2'

import { FeatureFlag, useIsFlagEnabled } from '@/hooks/useIsFlagEnabled'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { resetSession, selectCpxCard, selectDMPConnectSessionId } from '@/redux/reducers/dmp-connect.reducer'
import { CPxCardStatusEnum } from '@/core/types/dmp-connect'

export default function DMPConnectProvider({ children }: PropsWithChildren<{}>) {
  const isDmpConnectEnabled = useIsFlagEnabled(FeatureFlag.DmpConnect)

  return (
    <>
      {isDmpConnectEnabled && <DMPConnectSessionMonitor />}
      {children}
    </>
  )
}

/** CPS-card session monitoring + "card removed" snackbar; only mounted when the dmp-connect flag is on. */
function DMPConnectSessionMonitor() {
  const dispatch = useAppDispatch()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const cpxCard = useAppSelector(selectCpxCard)
  const sessionId = useAppSelector(selectDMPConnectSessionId)

  const { data: sessionExists, refetch } = useSessionExistsQuery(sessionId ?? skipToken)
  const { cpxCardMonitoringData, startMonitoring, stopMonitoring } = useStartCardMonitoring('cpx')

  const isSessionValid = sessionExists?.i_exists === 1
  const remaining = sessionExists?.s_remaining

  useEffect(() => {
    const remainingInMilliseconds = sessionRemainingToMilliseconds(remaining)
    if (!remainingInMilliseconds) return

    const timeoutId = setTimeout(() => {
      refetch()
    }, remainingInMilliseconds)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [refetch, remaining])

  useEffect(() => {
    if (!cpxCard || !isSessionValid) return
    startMonitoring(5)
    return () => {
      stopMonitoring()
    }
  }, [cpxCard, isSessionValid, startMonitoring, stopMonitoring])

  useEffect(() => {
    if (!cpxCardMonitoringData) return

    if (cpxCardMonitoringData.i_cardStatus === CPxCardStatusEnum.NO_CARD_INSERTED) {
      enqueueSnackbar(t`The CPS card was removed`, {
        action: (key) => (
          <>
            <Button component={Link} to="/configuration?tab=dmp-connect" color="inherit" onClick={() => closeSnackbar(key)} size="small">
              Go to settings
            </Button>
            <Button color="inherit" onClick={() => closeSnackbar(key)} size="small">
              Close
            </Button>
          </>
        ),
        persist: true,
        preventDuplicate: true,
        variant: 'error',
      })
      dispatch(resetSession())
    }
  }, [dispatch, cpxCardMonitoringData, closeSnackbar, enqueueSnackbar])

  return null
}

function sessionRemainingToMilliseconds(remaining?: string) {
  if (!remaining) return null

  // Split the string into hours, minutes, and seconds using regex
  const timeParts = remaining.match(/(\d+):(\d+)'(\d+)"/)

  if (!timeParts) return null

  // Parse hours, minutes, and seconds from the match groups
  const hours = parseInt(timeParts[1], 10)
  const minutes = parseInt(timeParts[2], 10)
  const seconds = parseInt(timeParts[3], 10)

  // Convert to milliseconds
  const milliseconds = hours * 60 * 60 * 1000 + minutes * 60 * 1000 + seconds * 1000

  return milliseconds
}
